import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { environment } from '../../../environments/environment';
import { LachesisService } from '../../analytics/services/lachesis.service';
import { DrawingTypes } from '../../documents/enums/drawing-types.enum';
import { ObserverService } from '../../ecaps-core/services/observer.service';
import { RevitEvents } from '../../external-communication/models/revit-events.enum';
import { RevitCommsService } from '../../external-communication/services/revit-comms.service';
import {
  EventCategories,
  GoogleAnalyticsService,
} from '../../google/services/google-analytics.service';
import { competitorCompareList } from './models/competitor-model-list.const';
import { ICompetitorCompItem } from './models/i-competitor-item.interface';
import { IModelItem } from './models/i-model-item.interface';
import { ModelList } from './models/model-list.const';

export interface IListItem {
  text: string;
  value: any;
}

@Component({
    selector: 'app-model-selection-page',
    templateUrl: './model-selection-page.component.html',
    styleUrls: ['./model-selection-page.component.scss'],
    standalone: false
})
export class ModelSelectionPageComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private modelList = ModelList;

  production = environment.production;

  formGroup: UntypedFormGroup;
  filterField: UntypedFormControl;
  modelGroupField: UntypedFormControl;
  productTypeField: UntypedFormControl;
  restoreTestJsonField: UntypedFormControl;
  manufacturerField: UntypedFormControl;

  filteredModelList: IModelItem[] = [];
  modelGroupList: IListItem[] = [];
  productTypeList: IListItem[] = [];
  manufacturersList: IListItem[] = [];

  constructor(
    private revitComms: RevitCommsService,
    private googleAnalytics: GoogleAnalyticsService,
    private lachesisService: LachesisService,
    private observer: ObserverService
  ) {
    this.filterField = new UntypedFormControl('');
    this.modelGroupField = new UntypedFormControl('');
    this.productTypeField = new UntypedFormControl('');
    this.restoreTestJsonField = new UntypedFormControl('');
    this.manufacturerField = new UntypedFormControl('');

    this.formGroup = new UntypedFormGroup({
      filter: this.filterField,
      modelGroup: this.modelGroupField,
      productType: this.productTypeField,
      restoreTestJson: this.restoreTestJsonField,
      manufacturer: this.manufacturerField,
    });

    this.updateFilteredModelList();

    this.getManufacturers();
  }

  ngOnInit() {
    Array.from(document.getElementsByClassName('logo')).forEach(
      (element: HTMLElement) => {
        element.addEventListener('click', () => {
          this.revitLogoClicked.call(this);
        });
      }
    );
  }

  ngAfterViewInit(): void {
    this.lazyLoadImages();
  }

  ngOnDestroy(): void {
    this.observer.clear();
  }

  updateFilteredModelList() {
    const compModels: ICompetitorCompItem[] = [];

    if (
      this.manufacturerField.value !== '' &&
      this.manufacturerField.value !== 'Greenheck' &&
      this.filterField.value !== ''
    ) {
      compModels.push(
        ...competitorCompareList
          .filter(
            (item) =>
              item.compName === this.manufacturerField.value ||
              item.trademark.indexOf(this.manufacturerField.value) > -1
          )
          .filter(
            (item) =>
              item.ghModel
                .toLowerCase()
                .trim()
                .indexOf(this.filterField.value.toLowerCase().trim()) > -1 ||
              item.compModel
                .toLowerCase()
                .trim()
                .indexOf(this.filterField.value.toLowerCase().trim()) > -1
          )
      );
    }

    const output = this.modelList
      .filter((item) => {
        if (compModels.length > 0) {
          return (
            compModels.filter((compItem) => {
              if (
                compItem.ghModel.toLowerCase().trim() ===
                item.model.toLowerCase().trim()
              ) {
                if (!!compItem.filter) {
                  let match = true;

                  Object.keys(compItem.filter).forEach((key) => {
                    if (compItem.filter[key] !== item[key]) {
                      match = false;
                    }
                  });

                  return match;
                } else {
                  return true;
                }
              } else {
                return false;
              }
            }).length > 0
          );
        } else {
          return true;
        }
      })
      .filter(
        (item) =>
          (item.name
            .toLowerCase()
            .indexOf(this.filterField.value.toLowerCase().trim()) > -1 &&
            compModels.length === 0) ||
          this.filterField.value.trim() === '' ||
          compModels.length > 0
      )
      .filter(
        (item) =>
          item.modelGroup === this.modelGroupField.value ||
          this.modelGroupField.value === ''
      )
      .filter(
        (item) =>
          item.productType === this.productTypeField.value ||
          this.productTypeField.value === ''
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    this.filteredModelList.splice(0);

    this.filteredModelList.push(...output);

    this.UpdateModelGroupList();

    this.updateProductTypeList();

    setTimeout(() => {
      this.lazyLoadImages();
    }, 100);
  }

  UpdateModelGroupList() {
    const filteredList = this.modelList.filter(
      (item) =>
        ((item.name
          .toLowerCase()
          .indexOf(this.filterField.value.toLowerCase().trim()) > -1 &&
          this.filterField.value !== '') ||
          this.filterField.value === '') &&
        (item.productType === this.productTypeField.value ||
          this.productTypeField.value === '')
    );

    const output: IListItem[] = [];

    if (!!filteredList) {
      filteredList
        .map((item) => item.modelGroup)
        .forEach((modelGroup) => {
          if (
            !!modelGroup &&
            output.findIndex((item) => item.value === modelGroup.trim()) === -1
          ) {
            output.push({ text: modelGroup.trim(), value: modelGroup.trim() });
          }
        });
    }

    output.sort((a, b) => a.text.localeCompare(b.text));

    this.modelGroupList.splice(0);

    this.modelGroupList.push({ text: 'Any', value: '' }, ...output);
  }

  updateProductTypeList() {
    const filteredList = this.modelList.filter(
      (item) =>
        ((item.name
          .toLowerCase()
          .indexOf(this.filterField.value.toLowerCase().trim()) > -1 &&
          this.filterField.value !== '') ||
          this.filterField.value === '') &&
        (item.modelGroup === this.modelGroupField.value ||
          this.modelGroupField.value === '')
    );

    const output: IListItem[] = [];

    if (!!filteredList) {
      filteredList
        .map((item) => item.productType)
        .forEach((productType) => {
          if (
            !!productType &&
            output.findIndex((item) => item.value === productType.trim()) === -1
          ) {
            output.push({
              text: productType.trim(),
              value: productType.trim(),
            });
          }
        });
    }

    output.sort((a, b) => a.text.localeCompare(b.text));

    this.productTypeList.splice(0);

    this.productTypeList.push({ text: 'Any', value: '' }, ...output);
  }

  downloadClick(model: IModelItem) {
    this.googleAnalytics.trackEvent_Old(
      EventCategories.revitAddin,
      'Model',
      'Download 200',
      model.name
    );

    this.googleAnalytics.trackEvent('Drawing', {
      model: model.modelGroup,
      modelName: model.name,
      action: 'Download',
      deliveryMethod: 'Download',
      contentType: 'LOD 200',
    });

    this.lachesisService.trackEvent({
      eventName: 'Drawings',
      model: model.name,
      retrievalMethod: 'Download',
      drawingType: DrawingTypes.revitLOD200,
      revitVersion: this.revitComms.revitVersion,
      revitPluginVersion: this.revitComms.revitPluginVersion,
    });

    this.revitComms.download(
      `${environment.drawingContentBase}${model.file}`,
      model.file
    );
  }

  insertClick(model: IModelItem) {
    this.googleAnalytics.trackEvent_Old(
      EventCategories.revitAddin,
      'Model',
      'Insert 200',
      model.name
    );

    this.googleAnalytics.trackEvent('Drawing', {
      model: model.modelGroup,
      modelName: model.name,
      action: 'Insert',
      deliveryMethod: 'Insert',
      contentType: 'LOD 200',
    });

    this.lachesisService.trackEvent({
      eventName: 'Drawings',
      model: model.name,
      retrievalMethod: 'Insert',
      drawingType: DrawingTypes.revitLOD200,
      revitVersion: this.revitComms.revitVersion,
      revitPluginVersion: this.revitComms.revitPluginVersion,
    });

    this.revitComms.insert(
      `${environment.drawingContentBase}${model.file}`,
      model.file
    );
  }

  revitLogoClicked() {
    this.filterField.setValue('');
    this.modelGroupField.setValue('');
    this.productTypeField.setValue('');

    this.updateFilteredModelList();
  }

  testRestore() {
    const restoreTestJson =
      this.restoreTestJsonField?.value ||
      '{"$type":"Greenheck.StateExport.StateExportResult, Greenheck","States":{"$type":"System.Collections.Generic.List`1[[Greenheck.StateExport.StateExportInfo, Greenheck]], mscorlib","$values":[{"$type":"Greenheck.StateExport.StateExportInfo, Greenheck","BusinessUnit":null,"EntityName":null,"Level":2,"Values":{"$type":"System.Collections.Generic.List`1[[Greenheck.StateExport.KeyValuePair, Greenheck]], mscorlib","$values":[{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"Tag","Value":"THISISTHETAGNAME"}]}},{"$type":"Greenheck.StateExport.StateExportInfo, Greenheck","BusinessUnit":"Fans","EntityName":"InlineSelector","Level":1,"Values":{"$type":"System.Collections.Generic.List`1[[Greenheck.StateExport.KeyValuePair, Greenheck]], mscorlib","$values":[{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"HousingStyle","Value":"NoPreference"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"DischargeRooftop","Value":"Any"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"HighWind","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"AllowMotorServiceFactor","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"OutletCone","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"SoundTrap","Value":"Unspecified"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"VibrationTest","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"AirFlow","Value":"Exhaust"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"PropellerMaterial","Value":"Any"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"GrillMounted","Value":"Any"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"HasInletBox","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"HasWallHousing","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"HasWeatherhood","Value":"None"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"ExhaustSystemFanQuantity","Value":"One"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"BypassAirPlenum","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"HasFanOnStandby","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"Terrain","Value":"FlatWaterDesert"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"MinimumNozzleVelocity","Value":"1000"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"FanVolumeToDesignVolumeRatio","Value":"1.1"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"FilterOptionNonsizing","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"RedundancyOption","Value":"None"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"Volume","Value":"5555"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"StaticPressure","Value":"0.2"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"Elevation","Value":"1"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"VoltageCyclePhase","Value":"208/60/1"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"ModelGroup","Value":"InlineFans"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"AirstreamTemperature","Value":"70"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"StartupTemperature","Value":"70"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"SmokeControl","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"SeismicRated","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"RestaurantExhaust","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"SparkRated","Value":"None"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"ForOutdoorUse","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"FanMaterials","Value":"Any"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"MountingOption","Value":"Horizontal"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"DuctTypeSelectionOption","Value":"Any"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"DriveLoss","Value":"Medium"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"SpeedControlled","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"ApplyVFD","Value":"None"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"DriveType","Value":"Any"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"AllowPerformanceBaffle","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"SideDischarge","Value":"None"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"FilterRequired","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"InletConditions","Value":"DuctedInlet"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"InletGuardSizingOption","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"HasBeltTube","Value":"Yes"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"OutletConditions","Value":"DuctedOutlet"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"OutletGuardSizingOption","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"HasDamper","Value":"None"}]}},{"$type":"Greenheck.StateExport.StateExportInfo, Greenheck","BusinessUnit":"Fans","EntityName":"RooftopExhaustConfig","Level":0,"Values":{"$type":"System.Collections.Generic.List`1[[Greenheck.StateExport.KeyValuePair, Greenheck]], mscorlib","$values":[{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"MotorEnclosure","Value":"ODP"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"MotorHorsePower","Value":"3/4"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"MotorPhase","Value":"1"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"Voltage","Value":"208"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"PowerFrequency","Value":"60"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"MotorType","Value":"AC"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"Arrangement","Value":"-"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"DischargePosition","Value":"-"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"WheelRotation","Value":"-"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"DriveType","Value":"Belt"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"AMCA_CertificationID","Value":"S"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"Model","Value":"BSQ"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"Size","Value":"24"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"SizeName","Value":"240"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"MinimumMotorSizeNema","Value":"0.75"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"MaximumMotorSize","Value":"7.5"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"OperatingFrequency","Value":"60"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"ModelGroup","Value":"InlineFans"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"WheelTypeActual","Value":"None"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"HasVFD","Value":"false"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"VoltageCyclePhase","Value":"208/60/1"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"AirstreamTemperature","Value":"70"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"HasSpeedController","Value":"false"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"SeismicRated","Value":"No"},{"$type":"Greenheck.StateExport.KeyValuePair, Greenheck","Key":"FanSelectionSizeId","Value":"2.042e492f737209a5601e1e9429696422160b4998"}]}}]}}';
    this.revitComms.localCommsService.comms.appToWeb(
      'Test ID',
      RevitEvents.reselect,
      restoreTestJson
    );
  }

  getManufacturers(): void {
    const list: string[] = [];

    competitorCompareList.forEach((item) => {
      list.push(...item.trademark);
    });

    list.push(...competitorCompareList.map((compItem) => compItem.compName));

    this.manufacturersList.push(
      ...list
        .filter((value, index, self) => self.indexOf(value) === index)
        .sort()
        .map((item) => {
          return {
            text: item.replace(/_/g, ' '),
            value: item,
          };
        })
    );

    this.manufacturerField.setValue('Greenheck');
  }

  getMatchModel(model: IModelItem): string {
    const compModel = competitorCompareList
      .filter((item) => item.compName === this.manufacturerField.value)
      .filter(
        (item) =>
          item.compModel
            .toLowerCase()
            .trim()
            .indexOf(this.filterField.value.toLowerCase().trim()) > -1
      )
      .find(
        (item) =>
          item.ghModel.toLowerCase().trim() === model.model.toLowerCase().trim()
      );

    return !!compModel ? compModel.compModel : 'Unknown';
  }

  private lazyLoadImages() {
    const cardList = document.querySelectorAll('.model-list .mat-mdc-card');

    if (cardList.length > 0) {
      const elementList = Array.from(cardList).filter(
        (element) => !!element.querySelector('.model-image.loading')
      );

      this.observer.observe(
        elementList,
        'intersection',
        (element, observing) => {
          if (observing) {
            const image = element.querySelector(
              '.model-image'
            ) as HTMLImageElement;

            if (!!image) {
              image.src = image.dataset.src;
            }
          }

          return false;
        }
      );
    }
  }
}
